import { FormEvent, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { BadgeCheck, ShieldCheck } from "lucide-react";
import { ErrorBox, Spinner } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  changeHostedAccountPassword,
  createPlatformPortalContext,
  getHostedAccount,
  getProjectPortalContext,
  revokeConnectedApplication,
  revokeAccountSessions,
  safeHostedImageURL,
  startGoogleProvider,
  unlinkGoogleProvider,
  updateHostedAccount,
  type AccountData,
  type HostedPortalContext,
} from "@/lib/hostedPortal";
import { api, type AgentGrant } from "@/lib/api";

export function AccountCenterPage() {
  const { projectSlug } = useParams();
  const location = useLocation();
  const transaction =
    new URLSearchParams(location.search).get("transaction") ?? "";
  const [context, setContext] = useState<HostedPortalContext | null>(null);
  const [account, setAccount] = useState<AccountData | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agentGrants, setAgentGrants] = useState<AgentGrant[]>([]);

  useEffect(() => {
    const request = projectSlug
      ? getProjectPortalContext(projectSlug, transaction)
      : createPlatformPortalContext("/account");
    void request
      .then(async (next) => {
        if (!next.authenticated)
          throw new Error("Sign in to open Account Center.");
        setContext(next);
        const data = await getHostedAccount(next);
        setAccount(data);
        setDisplayName(data.profile.display_name ?? "");
        setUsername(data.profile.username ?? "");
        setEmail(data.profile.email ?? "");
        setAvatarUrl(data.profile.avatar_url ?? "");
        if (!projectSlug) {
          try {
            const grants = await api.listAgentGrants();
            setAgentGrants(grants.data ?? []);
          } catch {
            setAgentGrants([]);
          }
        }
      })
      .catch(setError);
  }, [projectSlug, transaction]);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!context) return;
    setBusy(true);
    setError(null);
    try {
      const previousEmail = (account?.profile.email ?? "").trim().toLowerCase();
      const nextEmail = email.trim().toLowerCase();
      const previousUsername = account?.profile.username ?? "";
      const emailChanged = nextEmail !== previousEmail;
      const usernameChanged = username !== previousUsername;
      const profile = await updateHostedAccount(context, {
        display_name: displayName,
        ...(usernameChanged ? { username } : {}),
        ...(emailChanged ? { email: nextEmail || undefined } : {}),
        avatar_url: avatarUrl,
        current_password: currentPassword,
      });
      setAccount((value) => (value ? { ...value, profile } : value));
      setCurrentPassword("");
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (!context || context.realm !== "project") return;
    if (newPassword !== confirmPassword) {
      setError(new Error("New password and confirmation must match."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changeHostedAccountPassword(
        context,
        currentPassword,
        newPassword,
      );
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      window.location.assign(
        `/auth/${context.project_slug}/sign-in?transaction=${encodeURIComponent(context.transaction)}`,
      );
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  if (error && !account)
    return (
      <main className="mx-auto max-w-2xl p-8">
        <ErrorBox error={error} />
      </main>
    );
  if (!context || !account)
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <Spinner label="Loading Account Center…" center />
      </main>
    );
  const avatar = safeHostedImageURL(account.profile.avatar_url ?? undefined);
  return (
    <main className="mx-auto max-w-3xl space-y-6 p-6 sm:p-10">
      <header>
        <p className="eyebrow">{context.realm} identity</p>
        <h1 className="mt-2 font-sans text-3xl font-semibold">
          Account Center
        </h1>
        <p className="mt-2 text-sm text-text-muted">
          This page never combines identities from another Project or the
          Dashboard.
        </p>
      </header>
      {error ? <ErrorBox error={error} /> : null}
      <section className="card p-5">
        <div className="flex items-center gap-4">
          {avatar ? (
            <img
              src={avatar}
              alt=""
              referrerPolicy="no-referrer"
              className="h-16 w-16 rounded-full object-cover"
            />
          ) : (
            <div className="h-16 w-16 rounded-full bg-surface-container-low" />
          )}
          <div>
            <h2 className="font-semibold">
              {account.profile.display_name ||
                account.profile.email ||
                account.profile.username}
            </h2>
            <p className="text-sm text-text-muted">
              Created{" "}
              {new Date(account.profile.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <form onSubmit={save} className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="account-name">Display name</Label>
            <Input
              id="account-name"
              value={displayName}
              maxLength={100}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="account-username">Username</Label>
            <Input
              id="account-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="account-email">Email</Label>
            <Input
              id="account-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            {account.profile.email_verified ? (
              <p className="mt-1 text-xs text-text-muted">Email is verified.</p>
            ) : account.profile.email ? (
              <p className="mt-1 text-xs text-text-muted">
                Email is not verified. Changing email requires re-verification.
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="account-avatar">
              HTTPS avatar URL (png, jpg, webp)
            </Label>
            <Input
              id="account-avatar"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="account-password">
              Current password for username/email changes
            </Label>
            <Input
              id="account-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save profile"}
            </Button>
          </div>
        </form>
      </section>
      {projectSlug ? (
        <section className="card p-5">
          <h2 className="font-sans text-lg font-semibold">Change password</h2>
          <p className="mt-1 text-sm text-text-muted">
            Changing password signs you out of every session for this Project
            account. Dashboard identity is not affected.
          </p>
          <form
            onSubmit={changePassword}
            className="mt-4 grid gap-4 sm:grid-cols-2"
          >
            <div className="sm:col-span-2">
              <Label htmlFor="account-current-password">Current password</Label>
              <Input
                id="account-current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div>
              <Label htmlFor="account-new-password">New password</Label>
              <Input
                id="account-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <div>
              <Label htmlFor="account-confirm-password">Confirm password</Label>
              <Input
                id="account-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy}>
                {busy ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>
        </section>
      ) : null}
      {!projectSlug ? (
        <section className="card p-5">
          <h2 className="font-sans text-lg font-semibold">Connected agents</h2>
          <p className="mt-1 text-sm text-text-muted">
            AI agents authorized via OAuth for your projects. Revocation is
            immediate.
          </p>
          {agentGrants.length === 0 ? (
            <p className="mt-3 text-sm text-text-muted">No connected agents.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {agentGrants.map((g) => (
                <li
                  key={g.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded border p-3 text-sm"
                >
                  <div>
                    <div className="font-medium">{g.client_name}</div>
                    <div className="text-xs text-text-muted">
                      {g.project_name ?? g.project_id} ·{" "}
                      {(g.scopes ?? []).join(" ")}
                      {g.revoked_at ? " · revoked" : ""}
                    </div>
                  </div>
                  {!g.revoked_at ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        void (async () => {
                          setBusy(true);
                          try {
                            await api.revokeAgentGrant(g.id);
                            const next = await api.listAgentGrants();
                            setAgentGrants(next.data ?? []);
                          } catch (reason) {
                            setError(reason);
                          } finally {
                            setBusy(false);
                          }
                        })()
                      }
                    >
                      Revoke
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
      <section className="card p-5">
        <h2 className="font-sans text-lg font-semibold">Login methods</h2>
        <div className="mt-3 flex gap-2">
          <span className="badge">
            Password: {account.profile.password ? "enabled" : "not set"}
          </span>
          <span className="badge">
            Google: {account.profile.google ? "linked" : "not linked"}
          </span>
        </div>
        {context.realm === "project" ? (
          <div className="mt-4 flex gap-2">
            <Button
              variant="outline"
              onClick={() =>
                void startGoogleProvider(context, "link", currentPassword)
                  .then((r) => window.location.assign(r.authorization_url))
                  .catch(setError)
              }
            >
              Link Google
            </Button>
            {account.profile.google ? (
              <Button
                variant="outline"
                onClick={() =>
                  void unlinkGoogleProvider(context, currentPassword)
                    .then(() =>
                      setAccount({
                        ...account,
                        profile: { ...account.profile, google: false },
                      }),
                    )
                    .catch(setError)
                }
              >
                Unlink Google
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>
      <section className="card p-5">
        <h2 className="font-sans text-lg font-semibold">
          Connected Applications
        </h2>
        {account.applications.length === 0 ? (
          <p className="mt-3 text-sm text-text-muted">
            No authorization history in this realm.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {account.applications.map((app) => (
              <li
                key={app.application_id}
                className="rounded-lg border border-border-standard p-4"
              >
                <div className="flex justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-1 font-medium">
                      {app.name}
                      {app.eVerify ? (
                        <BadgeCheck
                          className="h-4 w-4 text-secondary"
                          aria-label="Verified Application"
                        />
                      ) : null}
                    </p>
                    <p className="text-xs text-text-muted">
                      {app.developer_name || "Unverified developer"} ·{" "}
                      {app.scopes.join(", ")}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    disabled={Boolean(app.revoked_at)}
                    onClick={() => {
                      if (window.confirm(`Revoke ${app.name}?`))
                        void revokeConnectedApplication(
                          context,
                          app.application_id,
                        )
                          .then(() =>
                            setAccount({
                              ...account,
                              applications: account.applications.map((item) =>
                                item.application_id === app.application_id
                                  ? {
                                      ...item,
                                      revoked_at: new Date().toISOString(),
                                    }
                                  : item,
                              ),
                            }),
                          )
                          .catch(setError);
                    }}
                  >
                    {app.revoked_at ? "Revoked" : "Revoke"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-sans text-lg font-semibold">
            Sessions and devices
          </h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() =>
                void revokeAccountSessions(context, "others")
                  .then(() =>
                    setAccount({
                      ...account,
                      sessions: account.sessions.filter(
                        (session) => session.current,
                      ),
                    }),
                  )
                  .catch(setError)
              }
            >
              Sign out others
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (
                  window.confirm("Sign out every session, including this one?")
                )
                  void revokeAccountSessions(context, "all")
                    .then(() =>
                      window.location.assign(
                        context.realm === "platform"
                          ? "/auth/sign-in"
                          : `/auth/${context.project_slug}/sign-in?transaction=${encodeURIComponent(context.transaction)}`,
                      ),
                    )
                    .catch(setError);
              }}
            >
              Sign out all
            </Button>
          </div>
        </div>
        {account.sessions.length === 0 ? (
          <p className="mt-3 text-sm text-text-muted">No sessions.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {account.sessions.map((session) => (
              <li
                key={session.display_id}
                className="flex items-center justify-between rounded-lg border border-border-standard p-3"
              >
                <div>
                  <p className="text-sm font-medium">
                    {session.device} {session.current ? "(current)" : ""}
                  </p>
                  <p className="text-xs text-text-muted">
                    {session.application || "Direct session"} ·{" "}
                    {session.network_hint || "network hidden"}
                  </p>
                </div>
                <Button
                  variant="outline"
                  disabled={Boolean(session.revoked_at)}
                  onClick={() =>
                    void revokeAccountSessions(
                      context,
                      "one",
                      session.display_id,
                    )
                      .then(() =>
                        setAccount({
                          ...account,
                          sessions: account.sessions.filter(
                            (item) => item.display_id !== session.display_id,
                          ),
                        }),
                      )
                      .catch(setError)
                  }
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="card p-5">
        <h2 className="font-sans text-lg font-semibold">
          Recent security history
        </h2>
        {account.history.length === 0 ? (
          <p className="mt-3 text-sm text-text-muted">
            No recent security events.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-line">
            {account.history.map((event) => (
              <li key={event.id} className="py-3 text-sm">
                <span className="font-medium">
                  {event.event_type.replaceAll("_", " ")}
                </span>
                <span className="ml-2 text-text-muted">
                  {event.outcome} ·{" "}
                  {new Date(event.occurred_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <p className="flex items-center gap-2 text-xs text-text-muted">
        <ShieldCheck className="h-4 w-4" /> Raw credentials and secret session
        identifiers are never displayed.
      </p>
    </main>
  );
}
